import type { Maybe } from '@adornis/base/utilTypes.js';
import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { html, nothing } from 'lit';
import { LodasConfiguration } from '../../../db/import-export/lodas-export.js';

const LODAS_TABS = [
  { type: 'stammdaten', label: 'Stammdaten' },
  { type: 'bewegdaten', label: 'Bewegungsdaten' },
] as const;

const SgLodasExportConfigurationContent = stateful(
  (props: { resolve: (config: LodasConfiguration) => void; reject: () => void }) => {
    // stable across rerenders; the entity defaults decide which options start switched on
    const [config] = useState(new LodasConfiguration({}), []);

    const form = useForm(config, { validator: null });
    const configuration = form.document;

    return html`
      <div class="dialog-container">
        ${DialogHeader({ title: 'LODAS-Export', close: () => props.reject(), closeLabel: 'Dialog schließen' })}

        <div role="tablist" class="tabs tabs-box">
          ${LODAS_TABS.map(
            tab => html`
              <button
                role="tab"
                class="tab ${configuration.type === tab.type ? 'tab-active' : ''}"
                @click=${() => form.field('type').onPicked?.(tab.type)}
              >
                ${tab.label}
              </button>
            `,
          )}
        </div>

        ${
          configuration.type === 'stammdaten'
            ? html`
                <div class="flex flex-col gap-2">
                  ${ToggleInput({ ...form.field('steuerdaten'), label: 'Steuerdaten' })}
                  ${ToggleInput({ ...form.field('tariftabelle'), label: 'Tariftabelle' })}
                  ${ToggleInput({ ...form.field('krankenkassen'), label: 'Krankenkassen' })}
                </div>
              `
            : nothing
        }

        <div class="flex justify-end gap-2">
          <button class="btn btn-outline" @click=${() => props.reject()}>Abbrechen</button>
          <button class="btn btn-primary" @click=${() => props.resolve(configuration)}>Export starten</button>
        </div>
      </div>
    `;
  },
);

/**
 * Asks which LODAS export to generate. Resolves to `null` when the dialog is dismissed.
 */
export const showLodasExportConfigurationDialog = async (): Promise<Maybe<LodasConfiguration>> => {
  try {
    return await showDialog<LodasConfiguration>({
      content: (resolve, reject) => SgLodasExportConfigurationContent({ resolve, reject }),
      modal: true,
      modalBoxClasses: 'max-w-md',
    });
  } catch {
    // dialog was dismissed
    return null;
  }
};
